import type { SxProps } from '@mui/material';
import type { OutlinedInputProps } from '@mui/material/OutlinedInput';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import { useField } from 'formik';

import { useLanguage } from '~/hooks/language';
import { mergeRefs, uncapitalize } from '~/utils';

import sx from './sx';

interface Props extends OutlinedInputProps {
  name: string;
  label: string;
  inputRef?: React.Ref<HTMLInputElement>;
  mui?: boolean;
  showErrorMessage?: boolean;
}

const TextField = (props: Props) => {
  const { name, label, disabled, mui = true, showErrorMessage = true, ...rest } = props;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const hiddenInputRef = useRef<HTMLInputElement | null>(null);
  const { translate } = useLanguage('ua');
  const [field, meta] = useField(name);
  const [isFocused, setIsFocused] = useState(false);
  const [width, setWidth] = useState(0);
  const theme = useTheme();
  const style = sx(theme);
  const hasErrorToShow = !!(meta.touched && meta.error);

  const helperText = useMemo(() => {
    if (!meta.error) return null;

    const isErrorArray = Array.isArray(meta.error);
    const errorText = !isErrorArray ? translate(`validations.${uncapitalize(meta.error)}`) : translate(meta.error);

    return Array.isArray(meta.error) ? (
      (meta.error as string[]).map(error => (
        <Typography component="span" sx={style.errorMessage} key={error}>
          {error}
        </Typography>
      ))
    ) : (
      <Typography component="span" sx={style.errorMessage}>
        {errorText}
      </Typography>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta.error]);

  useEffect(() => {
    setWidth(hiddenInputRef.current?.offsetWidth ?? 0);
  }, [field.value]);

  return (
    <Box sx={[style.textField, disabled && style.disabled, props.sx].filter(Boolean) as SxProps}>
      {mui ? (
        <FormControl variant="outlined" fullWidth error={hasErrorToShow} disabled={props.disabled}>
          <InputLabel size={props.size === 'small' ? 'small' : 'normal'} htmlFor={name}>
            {label}
          </InputLabel>
          <OutlinedInput
            id={name}
            {...field}
            {...{ name, label, disabled, ...rest }}
            inputRef={mergeRefs(inputRef, props.inputRef)}
          />
          {hasErrorToShow && showErrorMessage && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
      ) : (
        <>
          <span style={{ position: 'absolute', visibility: 'hidden', whiteSpace: 'nowrap' }} ref={hiddenInputRef}>
            {field.value}
          </span>
          <input
            {...field}
            style={{ width: `${(hiddenInputRef.current?.offsetWidth ?? width) + 10}px` }}
            type="text"
            ref={mergeRefs(inputRef, props.inputRef)}
            onFocus={() => {
              setIsFocused(true);
            }}
            onBlur={() => {
              setIsFocused(false);
            }}
          />
          <Box sx={{ ...style.underline, width: isFocused ? '100%' : '0' }} />
        </>
      )}
    </Box>
  );
};

export default TextField;
